import { useContext, useState } from 'react';
import {
  FormControl, FormControlLabel, FormHelperText, InputLabel, OutlinedInput, Stack, Switch,
} from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import InflationIndexService from '../../../shared/services/inflation-index/inflation-index.service';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const inflationIndexService = new InflationIndexService();

export const AddInflationIndexDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    setName(''); setSource(''); setSourceUrl(''); setCurrencyCode(''); setDescription('');
    setIsDefault(false); setErrors({});
    onClose();
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!source.trim()) e.source = 'Source is required';
    if (!currencyCode.trim()) e.currencyCode = 'Currency code is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await inflationIndexService.create({
        name: name.trim(),
        source: source.trim(),
        sourceUrl: sourceUrl.trim() || undefined,
        currencyCode: currencyCode.trim().toUpperCase(),
        description: description.trim() || undefined,
        isDefault,
      });
      onSuccess();
      handleClose();
    } catch (e: any) {
      userNotificationsContext.openSnackbar({ open: true, message: e?.response?.data?.message || 'Failed to create inflation index', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title="Add Inflation Index"
      cancelHandler={handleClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={isSubmitting}
    >
      <Stack sx={{ mt: 1 }} spacing={0.5}>
        <FormControl fullWidth error={!!errors.name}>
          <InputLabel>Name</InputLabel>
          <OutlinedInput label="Name" value={name} onChange={e => setName(e.target.value)} />
          <FormHelperText>{errors.name ?? ' '}</FormHelperText>
        </FormControl>
        <FormControl fullWidth error={!!errors.currencyCode}>
          <InputLabel>Currency Code</InputLabel>
          <OutlinedInput label="Currency Code" placeholder="GBP" value={currencyCode} onChange={e => setCurrencyCode(e.target.value)} />
          <FormHelperText>{errors.currencyCode ?? ' '}</FormHelperText>
        </FormControl>
        <FormControl fullWidth error={!!errors.source}>
          <InputLabel>Source</InputLabel>
          <OutlinedInput label="Source" placeholder="ONS" value={source} onChange={e => setSource(e.target.value)} />
          <FormHelperText>{errors.source ?? ' '}</FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Source URL (optional)</InputLabel>
          <OutlinedInput label="Source URL (optional)" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} />
          <FormHelperText> </FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Description (optional)</InputLabel>
          <OutlinedInput label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} />
          <FormHelperText> </FormHelperText>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />}
          label="Set as default for this currency"
        />
      </Stack>
    </DialogWrapper>
  );
};
